import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  TextField, 
  IconButton, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Tooltip,
  CircularProgress
} from '@mui/material';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search,
  ArrowLeft
} from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../Config/config';

const RolesManagement = () => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({ id: null, name: '', description: '' });

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE_URL}/api/nextgen-builder/roles`);
      if (res.data.success) setRoles(res.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (formData.id) {
        await axios.put(`${API_BASE_URL}/api/nextgen-builder/roles/${formData.id}`, formData);
      } else {
        await axios.post(`${API_BASE_URL}/api/nextgen-builder/roles`, formData);
      }
      setOpenDialog(false);
      fetchRoles();
      setFormData({ id: null, name: '', description: '' });
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this role?')) {
      try {
        await axios.delete(`${API_BASE_URL}/api/nextgen-builder/roles/${id}`);
        fetchRoles();
      } catch (err) { console.error(err); }
    }
  };

  return (
    <Box p={4} sx={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton 
            onClick={() => navigate('/nextgen/projects')} 
            sx={{ 
              color: '#843FEF', 
              bgcolor: 'white', 
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              '&:hover': { bgcolor: '#f0eaff' }
            }}
          >
            <ArrowLeft size={24} />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight="800" sx={{ color: '#1e293b', letterSpacing: '-0.02em' }}>
              Role Management
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Manage engineering roles and permissions
            </Typography>
          </Box>
        </Box>
        <Button 
          variant="contained" 
          startIcon={<Plus size={18} />}
          onClick={() => setOpenDialog(true)}
          sx={{ 
            bgcolor: '#843FEF', 
            '&:hover': { bgcolor: '#7035cc' }, 
            borderRadius: '12px', 
            px: 4,
            py: 1.2,
            textTransform: 'none',
            fontWeight: 'bold',
            boxShadow: '0 4px 14px 0 rgba(132, 63, 239, 0.39)'
          }}
        >
          Add Role
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={10}><CircularProgress sx={{ color: '#843FEF' }} /></Box>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <Table>
            <TableHead sx={{ bgcolor: '#f8fafc' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: '600', color: '#64748b', py: 2.5 }}>ROLE NAME</TableCell>
                <TableCell sx={{ fontWeight: '600', color: '#64748b' }}>DESCRIPTION</TableCell>
                <TableCell sx={{ fontWeight: '600', color: '#64748b' }} align="right">ACTIONS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.map((role) => (
                <TableRow key={role.id} hover sx={{ '& td': { py: 2, borderBottom: '1px solid #f1f5f9' } }}>
                  <TableCell>
                    <Typography fontWeight="700" color="#1e293b">{role.name}</Typography>
                  </TableCell>
                  <TableCell color="#475569">{role.description}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => { setFormData(role); setOpenDialog(true); }} sx={{ color: '#64748b' }}>
                      <Edit size={18} />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(role.id)}>
                      <Trash2 size={18} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="xs">
        <DialogTitle>{formData.id ? 'Edit Role' : 'Add Role'}</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField 
              label="Role Name" 
              fullWidth 
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
            />
            <TextField 
              label="Description" 
              fullWidth 
              multiline 
              rows={2}
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} sx={{ bgcolor: '#843FEF' }}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RolesManagement;
